import { useMemo } from 'react';
import { useRecentCareSources } from '../../../features/careSourceProcessing/useRecentCareSources';
import { useManualReviewQueueModel } from '../../../features/careSourceProcessing/useManualReviewQueueModel';
import { selectTodayPresentationEntries, selectRecentCareEntries } from './homeEntrySelectors';
import useProfileUpdateTimelineReceipts from '../../../features/careSourceProcessing/useProfileUpdateTimelineReceipts';

/**
 * Pure selector: turns a manual-review summary into a single conservative
 * "needs attention" signal. Only genuine pending review/confirmation work
 * counts — background processing status is never surfaced here.
 */
export const selectNeedsAttention = (summary = {}) => {
  const pendingTotal = Number(summary?.pendingTotal || 0);
  if (pendingTotal <= 0) {
    return null;
  }

  return {
    count: pendingTotal,
    label: pendingTotal === 1 ? 'update needs review' : 'updates need review',
  };
};

const HEALTH_REVIEW_DOMAINS = new Set(['allergy', 'medication']);

export const selectHealthReviewItems = (candidatesBySource = {}) => {
  const seen = new Set();
  return Object.entries(candidatesBySource)
    .flatMap(([sourceId, candidates]) => candidates.map((candidate) => ({ candidate, sourceId })))
    .filter(({ candidate }) => HEALTH_REVIEW_DOMAINS.has(candidate.domain))
    .filter(({ candidate }) => {
      const identity = [candidate.domain, String(candidate.label || '').trim().toLowerCase()].join(':');
      if (seen.has(identity)) return false;
      seen.add(identity);
      return true;
    })
    .map(({ candidate, sourceId }) => ({
      key: candidate.key,
      sourceId,
      label: candidate.label,
      domain: candidate.domain,
      typeLabel: candidate.domain === 'medication' ? 'Medication' : 'Allergy',
    }));
};

/**
 * Composes Home's data entirely from already-loaded Dashboard state
 * (usePanelDashboard) plus the existing manual-review queue model.
 * No new Firestore queries are introduced beyond the ones the review
 * queue already performs for the same childId elsewhere in the app.
 */
export const useHomeScreenData = (activeChild, hook, { referenceDate = new Date() } = {}) => {
  const childId = activeChild?.id || '';
  const activeChildEntries = useMemo(
    () => hook?.allEntries?.[childId] || [],
    [hook?.allEntries, childId]
  );
  const viewerRole = hook?.getUserRoleForChild?.(childId) || null;
  const canResolveHealthReview = ['care_owner', 'care_partner'].includes(viewerRole);
  const { receipts: profileUpdateReceipts } = useProfileUpdateTimelineReceipts({
    childId,
    viewerRole,
  });

  const todayEntries = useMemo(
    () => selectTodayPresentationEntries(activeChildEntries, referenceDate, 3, {
      profileUpdateReceipts,
      personName: activeChild?.preferredName || activeChild?.name || activeChild?.firstName || 'Profile',
    }),
    [activeChild, activeChildEntries, profileUpdateReceipts, referenceDate]
  );
  const recentCareEntries = useMemo(
    () => selectRecentCareEntries(activeChildEntries, referenceDate, 3),
    [activeChildEntries, referenceDate]
  );

  const { sources } = useRecentCareSources({
    childId,
    includeAllMatching: true,
  });
  const manualReview = useManualReviewQueueModel(sources);
  const healthReviewItems = useMemo(
    () => selectHealthReviewItems(manualReview.candidatesBySource),
    [manualReview.candidatesBySource]
  );
  const needsAttention = useMemo(
    () => healthReviewItems.length ? {
      count: healthReviewItems.length,
      label: healthReviewItems.length === 1 ? 'health detail needs review' : 'health details need review',
      items: healthReviewItems.slice(0, 2),
      hasMore: healthReviewItems.length > 2,
      canResolve: canResolveHealthReview,
    } : null,
    [canResolveHealthReview, healthReviewItems]
  );

  return {
    activeChildEntries,
    todayEntries,
    recentCareEntries,
    needsAttention,
    careSourceCount: sources.length,
  };
};

export default useHomeScreenData;
